import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { Badge, Box, IconButton, TextField, Typography } from 'seagull';
import { Trash } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import FormlingContainer from '../../AgreementStudioModal/FormlingContainer/FormlingContainer';
import { normalizeVariableLabel } from '../TemplateVariables.utils';
import { useTemplateVariables } from '../TemplateVariablesContext';
import styles from './TemplateVariablesSection.module.scss';
export const GetTemplateVariablesSectionTID = (parentTestId)=>getTestIds(parentTestId);
export function TemplateVariablesSection(param) {
    let { wasSubmitted, isOpen, setIsOpen, testId } = param;
    const { TemplateVariablesSectionTestIds } = useMemo(()=>GetTemplateVariablesSectionTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('components.templateVariables.section');
    const { customVariables, templateVariableValues, setTemplateVariableValue, unresolvedCustomVariables, setShowVariableErrors } = useTemplateVariables();
    useEffect(()=>{
        setShowVariableErrors(wasSubmitted);
    }, [
        wasSubmitted,
        setShowVariableErrors
    ]);
    const hasCustomVariables = customVariables.length > 0;
    const unresolvedNormalizedSet = useMemo(()=>new Set(unresolvedCustomVariables.map(normalizeVariableLabel)), [
        unresolvedCustomVariables
    ]);
    if (!hasCustomVariables) {
        return null;
    }
    const hasUnresolved = unresolvedNormalizedSet.size > 0;
    const badgeText = hasUnresolved ? t.badgeNeedValues({
        count: unresolvedNormalizedSet.size
    }) : t.badgeAllSet();
    const badgeSkin = hasUnresolved ? 'warning' : 'success';
    const headerBadge = /*#__PURE__*/ _jsx(Badge, {
        skin: badgeSkin,
        testId: TemplateVariablesSectionTestIds.BadgeText(),
        children: badgeText
    });
    return /*#__PURE__*/ _jsx("div", {
        className: styles.root,
        "data-testid": TemplateVariablesSectionTestIds.Root(),
        children: /*#__PURE__*/ _jsx(FormlingContainer, {
            title: t.title(),
            togglePanelView: true,
            isOpen: isOpen,
            setIsOpen: setIsOpen,
            testId: testId,
            headerExtra: headerBadge,
            children: /*#__PURE__*/ _jsxs(Box, {
                className: styles.sectionContent,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "secondary",
                        testId: TemplateVariablesSectionTestIds.ExplanatoryText(),
                        children: t.explanatoryText()
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.variablesGroup,
                        testId: TemplateVariablesSectionTestIds.CustomVariablesGroup(),
                        children: customVariables.map((label)=>{
                            var _templateVariableValues_normalized;
                            const normalized = normalizeVariableLabel(label);
                            const value = (_templateVariableValues_normalized = templateVariableValues[normalized]) !== null && _templateVariableValues_normalized !== void 0 ? _templateVariableValues_normalized : '';
                            const showError = wasSubmitted && unresolvedNormalizedSet.has(normalized);
                            return /*#__PURE__*/ _jsxs("div", {
                                className: styles.customVariableRow,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.customVariableField,
                                        children: /*#__PURE__*/ _jsx(TextField, {
                                            label: label,
                                            value: value,
                                            onChange: (e)=>setTemplateVariableValue(label, e.target.value),
                                            testId: TemplateVariablesSectionTestIds.CustomVariableField(label),
                                            error: showError,
                                            helperText: showError ? t.fieldError() : undefined
                                        })
                                    }),
                                    value && /*#__PURE__*/ _jsx(IconButton, {
                                        className: styles.customVariableClearButton,
                                        noPadding: true,
                                        skin: "transparent",
                                        size: "small",
                                        testId: TemplateVariablesSectionTestIds.CustomVariableClear(label),
                                        onClick: ()=>setTemplateVariableValue(label, ''),
                                        children: /*#__PURE__*/ _jsx(Trash, {
                                            size: 18
                                        })
                                    })
                                ]
                            }, normalized);
                        })
                    })
                ]
            })
        })
    });
}
